/** Section ids, order, headings and render-time numbering for the OIA draft (contract section 2). The page, outline and DOCX all read it. */
const FINDINGS = "findings."

/** The group heading drawn once above maintainability, points for determination and every finding. */
export const DISCUSSION_GROUP = "DISCUSSION AND FINDINGS"

/** Ground titles from GET /draft (`titles`), keyed by section id ("findings.G1"). */
export type SectionTitles = Record<string, string> | undefined

/** Before the discussion group. `submissions` is optional and listed only when present. */
export const HEAD_SECTIONS = ["caption", "preamble", "parties", "introduction", "background", "grounds_of_appeal", "submissions", "hearing"] as const
/** The group's fixed members; `findings.*` follow them. */
export const GROUP_FIXED = ["maintainability", "points"] as const
/** After the findings. */
export const TAIL_SECTIONS = ["leadin", "order", "closing"] as const

const FIXED: Record<string, string> = {
  preamble: "PREAMBLE",
  background: "STATEMENT OF FACTS",
  grounds_of_appeal: "GROUNDS OF APPEAL",
  submissions: "RESPONDENT'S SUBMISSIONS",
  hearing: "PERSONAL HEARING",
  order: "ORDER",
}

export interface SectionHeading {
  /** Canonical heading text; empty when the section has no heading of its own. */
  text: string
  /** 1 for a main heading, 2 for a run-in sub-heading ("(i) Whether ..."). */
  level: 1 | 2
  /** Set on every section inside "DISCUSSION AND FINDINGS"; the group heading is drawn once above the first of them. */
  group?: string
  /** PREAMBLE and ORDER are centred and underlined. */
  center?: boolean
  /** The body is centred too (the caption block). */
  centerBody?: boolean
}

const ROMAN: Array<[number, string]> = [[40, "xl"], [10, "x"], [9, "ix"], [5, "v"], [4, "iv"], [1, "i"]]
/** Lower-case roman numeral for 1..49 (grounds are few). */
export function roman(n: number): string {
  let out = ""
  let rest = Math.max(1, n)
  for (const [v, s] of ROMAN) while (rest >= v) { out += s; rest -= v }
  return out
}

/** 1-based position of a findings id among the findings on the page. */
function pointNumber(id: string, ids: readonly string[]): number {
  return ids.filter((x) => x.startsWith(FINDINGS)).indexOf(id) + 1
}

/** "(i) Whether ..." run-in sub-head for findings.X, from the ground's point; "(i)" alone when the title is missing. */
export function pointHeading(id: string, ids: readonly string[], titles?: SectionTitles): string {
  const title = titles?.[id]?.trim()
  const r = `(${roman(pointNumber(id, ids))})`
  return title ? `${r} ${title}` : r
}

/**
 * GET /draft clips a long ground title to 90 characters and ends it with "...". The points section already holds the whole
 * question as its lettered item, so a clipped title is swapped for the item it is the start of. Other titles are kept as they are.
 */
export function fullTitles(titles: SectionTitles, pointsMarkdown?: string): SectionTitles {
  if (!titles) return titles
  const items = splitBlocks(stripLeadingHeading(pointsMarkdown ?? ""))
    .flatMap((b) => (b.kind === "para" ? b.lines : []))
    .filter((l) => LETTERED.test(l))
    .map((l) => l.replace(LETTERED, "").replace(/\?\s*$/, "").trim())
  const out: Record<string, string> = { ...titles }
  for (const [id, raw] of Object.entries(titles)) {
    const title = raw?.trim()
    if (!title || !/(\.\.\.|…)$/.test(title)) continue
    const prefix = title.replace(/(\.\.\.|…)$/, "").trimEnd()
    const full = items.find((x) => x.startsWith(prefix) && x.length > prefix.length)
    if (full) out[id] = full
  }
  return out
}

const IN_GROUP = (id: string) => id === "maintainability" || id === "points" || id.startsWith(FINDINGS)
const NOT_IN_OUTLINE = new Set(["caption", "preamble", "parties", "order", "closing"])

/**
 * The order's outline, as signed orders number it: every main heading takes the next whole number ("2. STATEMENT OF
 * FACTS"), DISCUSSION AND FINDINGS takes one for all of it, and each titled sub-section inside it (maintainability,
 * the points, each point) is n.m. A section with no heading of its own (the introduction, the lead-in to the order)
 * is a whole-numbered paragraph too ("7. In view of the above findings, I pass the following order:", then the
 * centred ORDER). Caption, preamble, parties, the order and the closing are outside it.
 */
export function outline(ids: readonly string[], has: (id: string) => boolean = () => true): { unit: Map<string, string>; headed: Set<string>; group: number | null } {
  const unit = new Map<string, string>()
  const headed = new Set<string>()
  let n = 0
  let group: number | null = null
  let sub = 0
  for (const id of ids) {
    // A section with no text yet takes no number, so the numbers never jump (the .docx leaves it out).
    if (NOT_IN_OUTLINE.has(id) || !has(id)) continue
    if (IN_GROUP(id)) {
      if (group === null) group = ++n
      unit.set(id, `${group}.${++sub}`)
      headed.add(id)
    } else {
      unit.set(id, String(++n))
      if (FIXED[id]) headed.add(id)
    }
  }
  return { unit, headed, group }
}

/** Canonical headings for the given section ids, numbered as the order numbers them. Sections without a heading (caption, parties, introduction, leadin, closing) get an empty text. */
export function sectionHeadings(ids: readonly string[], titles?: SectionTitles, has?: (id: string) => boolean): Record<string, SectionHeading> {
  const { unit, group } = outline(ids, has)
  const discussion = group !== null ? `${group}. ${DISCUSSION_GROUP}` : DISCUSSION_GROUP
  const out: Record<string, SectionHeading> = {}
  for (const id of ids) {
    const at = unit.get(id)
    const num = (sep: string) => (at ? `${at}${sep}` : "")
    if (id === "caption") out[id] = { text: "", level: 1, centerBody: true }
    else if (id === "maintainability") out[id] = { text: `${num(" ")}Maintainability of the appeal`, level: 2, group: discussion }
    else if (id === "points") out[id] = { text: `${num(" ")}Points for determination`, level: 2, group: discussion }
    else if (id.startsWith(FINDINGS)) out[id] = { text: `${num(" ")}${pointHeading(id, ids, titles)}`, level: 2, group: discussion }
    else if (id === "preamble" || id === "order") out[id] = { text: FIXED[id], level: 1, center: true }
    else out[id] = { text: FIXED[id] ? `${num(". ")}${FIXED[id]}` : "", level: 1 }
  }
  return out
}

const LABELS: Record<string, string> = {
  caption: "Caption",
  preamble: "Preamble",
  parties: "Parties",
  introduction: "Introduction",
  background: "Statement of facts",
  grounds_of_appeal: "Grounds of appeal",
  submissions: "Respondent's submissions",
  hearing: "Personal hearing",
  maintainability: "Maintainability",
  points: "Points for determination",
  leadin: "Order lead-in",
  order: "Order",
  closing: "Closing",
}

/** Sentence-case label for outline links, aria labels and dialogs ("Point 1: Whether ..."). */
export function sectionLabel(id: string, ids?: readonly string[], titles?: SectionTitles): string {
  if (id.startsWith(FINDINGS)) {
    const n = pointNumber(id, ids ?? [id])
    const title = titles?.[id]?.trim()
    return title ? `Point ${n}: ${title}` : `Point ${n}`
  }
  return LABELS[id] ?? id
}

/**
 * Canonical order (contract section 2): caption, preamble, parties, introduction, background, grounds_of_appeal,
 * submissions (only when present), hearing, maintainability, points, findings.* in id order, leadin, order, closing,
 * then anything unknown. The fixed sections are always listed, so an undrafted section shows as a placeholder.
 */
export function canonicalIds(present: Iterable<string>): string[] {
  const have = new Set(present)
  const findings = [...have]
    .filter((id) => id.startsWith(FINDINGS))
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
  const known = new Set<string>([...HEAD_SECTIONS, ...GROUP_FIXED, ...findings, ...TAIL_SECTIONS])
  const unknown = [...have].filter((id) => !known.has(id)).sort()
  return [
    ...HEAD_SECTIONS.filter((id) => id !== "submissions" || have.has(id)),
    ...GROUP_FIXED,
    ...findings,
    ...TAIL_SECTIONS,
    ...unknown,
  ]
}
export const ordered = canonicalIds

/**
 * Removes a leading markdown heading line (the backend strips it too; this guards older saved text) and, through
 * breakLetteredLines, puts each lettered clause on its own line. Every page and DOCX path reads section text through it.
 */
export function stripLeadingHeading(markdown: string): string {
  return breakLetteredLines(markdown.replace(/^\s*#{1,6}[ \t]+[^\n]*(?:\n|$)/, "").replace(/^\s*\n/, ""))
}

const NOT_PLAIN_LINE = /^(\s*([-*•]|\d+[.)])\s|\||>|#)/
/**
 * A lettered clause ("(i) Whether ...") written on the line after plain text is a paragraph of its own. The editor and the
 * DOCX join single line breaks, so the blank line is added here; the clause stays in the same numbered paragraph.
 */
export function breakLetteredLines(markdown: string): string {
  const out: string[] = []
  for (const line of markdown.split("\n")) {
    const prev = out[out.length - 1]
    if (prev !== undefined && prev.trim() && !NOT_PLAIN_LINE.test(prev) && LETTERED.test(line.trim())) out.push("")
    out.push(line)
  }
  return out.join("\n")
}

/* ------------------------------------------------------------------------------------------------
 * Render-time paragraph numbering (see `outline`)
 * Under a numbered heading every paragraph is n.1, n.2 ("2. STATEMENT OF FACTS" -> 2.1, 2.2); inside the discussion each
 * sub-section's paragraphs are n.m.1, n.m.2. A section with no heading (introduction, lead-in) is a whole-numbered
 * paragraph: its first paragraph is n, any more n.1, n.2. Caption, preamble, parties, order and closing are unnumbered,
 * as are headings, tables, quotes and lettered clauses "(i)". Typed leading numbers are stripped.
 * ---------------------------------------------------------------------------------------------- */

export type BlockKind = "heading" | "table" | "quote" | "list" | "para"

export interface MdBlock {
  kind: BlockKind
  /** para/quote/heading/table: the source lines. list: one entry per top-level item (indented continuation lines joined with "\n"). */
  lines: string[]
}

export interface PlannedBlock extends MdBlock {
  /** One label per rendered target: a single entry for a para/heading/table/quote, one per item for a list. null = unnumbered. */
  labels: (string | null)[]
  /** Numbered paragraphs strip typed numbers; false once the closing tail starts (the copy-to list keeps its own numbers). */
  tail: boolean
}

const UNNUMBERED = new Set(["caption", "preamble", "parties", "order", "closing"])
const LIST_ITEM = /^(\s*)([-*•]|\d+[.)])\s+(.*)$/
const LETTERED = /^\(([a-z]|[ivxlc]+)\)\s/i
const TYPED_NUMBER = /^(\d{1,3}(?:\.\d+)+[.)]?|\d{1,3}[.)])\s+/

/** Removes a typed leading number such as "6.", "5.2." or "12)" (bodies should carry none). */
export function stripTypedNumber(text: string): string {
  return text.replace(TYPED_NUMBER, "")
}

/** Splits section markdown into the blocks the editor renders: paragraphs, lists, tables, quotes and headings. */
export function splitBlocks(markdown: string): MdBlock[] {
  const lines = markdown.replace(/\r\n?/g, "\n").split("\n")
  const blocks: MdBlock[] = []
  let para: string[] = []
  const flush = () => {
    if (para.length) blocks.push({ kind: "para", lines: para })
    para = []
  }
  let i = 0
  while (i < lines.length) {
    const line = lines[i]
    const t = line.trim()
    if (!t) { flush(); i++; continue }
    if (/^#{1,6}\s/.test(t)) { flush(); blocks.push({ kind: "heading", lines: [t] }); i++; continue }
    if (t.startsWith("|")) {
      flush()
      const rows: string[] = []
      while (i < lines.length && lines[i].trim().startsWith("|")) rows.push(lines[i++].trim())
      blocks.push({ kind: "table", lines: rows })
      continue
    }
    if (t.startsWith(">")) {
      flush()
      const rows: string[] = []
      while (i < lines.length && lines[i].trim().startsWith(">")) rows.push(lines[i++].trim().replace(/^>\s?/, ""))
      blocks.push({ kind: "quote", lines: rows })
      continue
    }
    const m = line.match(LIST_ITEM)
    if (m && m[1].length < 2) {
      flush()
      const items: string[] = []
      while (i < lines.length) {
        const cur = lines[i]
        if (!cur.trim()) {
          // A blank line inside a loose list: the list goes on only if another top-level item follows.
          let j = i
          while (j < lines.length && !lines[j].trim()) j++
          const next = lines[j]?.match(LIST_ITEM)
          if (next && next[1].length < 2) { i = j; continue }
          break
        }
        const im = cur.match(LIST_ITEM)
        if (im && im[1].length < 2) items.push(im[3].trim())
        else if (/^\s{2,}\S/.test(cur) && items.length) items[items.length - 1] += `\n${cur.trim()}`
        else break
        i++
      }
      blocks.push({ kind: "list", lines: items })
      continue
    }
    para.push(t)
    i++
  }
  flush()
  return blocks
}

/** Numbers every section with the render-time scheme. `sections` must be in canonical order. */
export function planNumbering(sections: ReadonlyArray<{ id: string; markdown: string }>): Map<string, PlannedBlock[]> {
  const out = new Map<string, PlannedBlock[]>()
  const text = new Map(sections.map((x) => [x.id, stripLeadingHeading(x.markdown).trim()]))
  const { unit, headed } = outline(sections.map((x) => x.id), (id) => !!text.get(id))
  for (const { id, markdown } of sections) {
    const u = unit.get(id)
    const numbered = !!u && !UNNUMBERED.has(id)
    const isClosing = id === "closing"
    let k = 0
    // Headed: n.1, n.2 ... Headless: the first paragraph is the whole number, any more n.1, n.2 ...
    const next = () => {
      k += 1
      return headed.has(id) ? `${u}.${k}` : k === 1 ? String(u) : `${u}.${k - 1}`
    }
    const planned: PlannedBlock[] = []
    for (const raw of splitBlocks(stripLeadingHeading(markdown))) {
      const block: PlannedBlock = { ...raw, labels: raw.kind === "list" ? raw.lines.map(() => null) : [null], tail: isClosing && raw.kind !== "heading" }
      planned.push(block)
      if (!numbered || block.tail) continue
      if (block.kind === "para") {
        if (!LETTERED.test(block.lines[0])) block.labels[0] = next()
      } else if (block.kind === "list") {
        block.lines.forEach((_, t) => { block.labels[t] = next() })
      }
    }
    out.set(id, planned)
  }
  return out
}

/** The code-built placeholder in the running header (the caption itself comes from the `caption` section). */
export const OIA_NO_PLACEHOLDER = "[OIA No.]"
